<template>
    <div v-if="informations.dcmInfo">
        <div class="row margin-top-20">
            <div class="col-12">
                <base-checkbox v-model="informations.dcmInfo.urgent" :disabled="disabled">
                    {{ $t('dcm.urgent') }}
                </base-checkbox>
            </div>
        </div>

        <label class="label-title margin-top-20">{{ $t('dcm.applicantTitle') }}</label>
        <div class="row">
            <div class="col-sm-6" v-for="field in applicantFields" :key="field">
                <base-input
                    :disabled="disabled"
                    :label="$t('dcm.applicant.' + field)"
                    :placeholder="$t('dcm.applicant.' + field)"
                    v-model="informations.dcmInfo.applicant[field]">
                </base-input>
            </div>
        </div>

        <label class="label-title margin-top-20">{{ $t('dcm.identificationTitle') }}</label>
        <div class="row">
            <div class="col-sm-6">
                <base-input
                    :disabled="disabled"
                    :label="$t('dcm.projectNumber')"
                    :placeholder="$t('dcm.projectNumber')"
                    v-model="informations.dcmInfo.projectNumber">
                </base-input>
            </div>
            <div class="col-sm-6">
                <base-input
                    :disabled="disabled"
                    :label="$t('dcm.sketchNumber')"
                    :placeholder="$t('dcm.sketchNumber')"
                    v-model="informations.dcmInfo.sketchNumber">
                </base-input>
            </div>
            <div class="col-sm-6" v-for="choice in identificationChoices" :key="choice">
                <label>{{ $t('dcm.' + choice) }}</label>
                <el-select
                    class="select-primary"
                    size="large"
                    clearable
                    :disabled="disabled"
                    :placeholder="$t('dcm.' + choice)"
                    v-model="informations.dcmInfo[choice]">
                    <el-option
                        v-for="option in yesNoOptions"
                        class="select-primary"
                        :value="option.value"
                        :label="option.label"
                        :key="option.value">
                    </el-option>
                </el-select>
            </div>
            <div class="col-12">
                <base-input
                    :disabled="disabled"
                    :label="$t('dcm.specialRequests')"
                    :placeholder="$t('dcm.specialRequests')"
                    v-model="informations.dcmInfo.specialRequests">
                </base-input>
            </div>
        </div>

        <label class="label-title margin-top-20">{{ $t('dcm.contractorTitle') }}</label>
        <base-button v-if="!disabled" type="info" link size="sm" @click="copyApplicant">
            <i class="tim-icons icon-single-copy-04"></i>
            <label style="font-size: 12px; margin-bottom:0; cursor:pointer;">
                {{ $t('dcm.copyApplicant') }}
            </label>
        </base-button>
        <div class="row">
            <div class="col-sm-6" v-for="field in contractorFields" :key="field">
                <base-input
                    :disabled="disabled"
                    :label="$t('dcm.contractor.' + field)"
                    :placeholder="$t('dcm.contractor.' + field)"
                    v-model="informations.dcmInfo.contractor[field]">
                </base-input>
            </div>
        </div>

        <label class="label-title margin-top-20">{{ $t('dcm.areaTitle') }}</label>
        <div class="row">
            <div class="col-sm-6 col-md-3" v-for="field in areaFields" :key="field">
                <base-input
                    :disabled="disabled"
                    :label="$t('dcm.area.' + field)"
                    :placeholder="$t('dcm.area.' + field)"
                    v-model="informations.dcmInfo.area[field]">
                </base-input>
            </div>
        </div>

        <label class="label-title margin-top-20">{{ $t('dcm.consentTitle') }}</label>
        <div class="row">
            <div class="col-sm-6" v-for="choice in consentChoices" :key="choice">
                <label>{{ $t('dcm.' + choice) }}</label>
                <el-select
                    class="select-primary"
                    size="large"
                    clearable
                    :disabled="disabled"
                    :placeholder="$t('dcm.' + choice)"
                    v-model="informations.dcmInfo[choice]">
                    <el-option
                        v-for="option in yesNoOptions"
                        class="select-primary"
                        :value="option.value"
                        :label="option.label"
                        :key="option.value">
                    </el-option>
                </el-select>
            </div>
            <div class="col-sm-6 project-label create-project-date">
                <base-input :label="$t('dcm.consentDate')">
                    <el-date-picker
                        type="daterange"
                        format="dd/MM/yyyy"
                        value-format="yyyy-MM-dd"
                        range-separator="-"
                        clearable
                        :disabled="disabled"
                        :start-placeholder="$t('dcm.consentFrom')"
                        :end-placeholder="$t('dcm.consentUntil')"
                        v-model="consentRange">
                    </el-date-picker>
                </base-input>
            </div>
        </div>
    </div>
</template>

<script>
import { Select, Option, DatePicker } from 'element-ui';
import moment from 'moment';

const defaultDcmInfo = () => ({
    urgent: false,
    applicant: { name: '', roleCompany: '', address: '', city: '', phone: '', email: '' },
    projectNumber: '',
    sketchNumber: '',
    excavation: '',
    otherDocuments: '',
    specialRequests: '',
    contractor: { name: '', address: '', phone: '', email: '' },
    area: { total: '', paving: '', sidewalk: '', other: '' },
    trafficPlan: '',
    consentAnnex: '',
    consentDate: '',
    consentEndDate: ''
});

export default {
    name: 'DcmComponent',
    props: ['informations', 'disabled'],
    components: {
        [Option.name]: Option,
        [Select.name]: Select,
        [DatePicker.name]: DatePicker
    },
    data() {
        return {
            applicantFields: ['name', 'roleCompany', 'address', 'city', 'phone', 'email'],
            identificationChoices: ['excavation', 'otherDocuments'],
            contractorFields: ['name', 'address', 'phone', 'email'],
            areaFields: ['total', 'paving', 'sidewalk', 'other'],
            consentChoices: ['trafficPlan', 'consentAnnex']
        };
    },
    computed: {
        yesNoOptions() {
            return [
                { value: 'yes', label: this.$t('dcm.yes') },
                { value: 'no', label: this.$t('dcm.no') }
            ];
        },
        consentRange: {
            get() {
                const { consentDate, consentEndDate } = this.informations.dcmInfo;
                if (!consentDate) return null;
                return [consentDate, consentEndDate || moment(consentDate).add(6, 'months').format('YYYY-MM-DD')];
            },
            set(range) {
                const [start, end] = range || ['', ''];
                this.informations.dcmInfo.consentDate = start;
                this.informations.dcmInfo.consentEndDate = end;
            }
        }
    },
    methods: {
        copyApplicant() {
            const { name, address, city, phone, email } = this.informations.dcmInfo.applicant;
            const copy = { name, address: [address, city].filter(Boolean).join(', '), phone, email };
            Object.keys(copy).forEach((key) => {
                if (copy[key]) this.informations.dcmInfo.contractor[key] = copy[key];
            });
        }
    },
    watch: {
        // Both project forms replace the informations object when a saved project loads,
        // and projects saved before this section existed have no dcmInfo.
        informations: {
            immediate: true,
            handler(informations) {
                if (!informations) return;
                const base = defaultDcmInfo();
                const saved = informations.dcmInfo || {};
                this.$set(informations, 'dcmInfo', {
                    ...base,
                    ...saved,
                    applicant: { ...base.applicant, ...saved.applicant },
                    contractor: { ...base.contractor, ...saved.contractor },
                    area: { ...base.area, ...saved.area }
                });
            }
        }
    }
};
</script>
<style>
  .el-date-editor.el-range-editor.el-input__inner.el-date-editor--daterange {
    padding-top: 3px !important;
  }
</style>
